import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UIRouter } from '@uirouter/angular';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { UserService } from 'src/app/Account/user.service';
import { FeatureFlagsService } from 'src/app/Common/FeatureFlags/feature-flags-service';
import { InstanceRights, RestrictService } from 'src/app/Common/Permissions/restrict.service';
import { ContactModalOrigin, EntityTypeIds } from 'src/app/constants.new';
import { InstanceRepository } from 'src/app/Entity/Instance/instance.repository';
import { CurrentEntityService } from '../../Common/Entity/CurrentEntityService';
import { IWeissmanModalComponent } from '../../Compliance/WeissmanModalService';
import { AssessorCollectorRepository, ParcelRepository, SiteRepository } from '../../Core-Repositories';
import { ContactRoleService } from '../../Role/contact.role.service';
import { TabSetInput } from '../../UI-Lib/ACE/Tab-Set/tabSet.component';
import { MessageModalService } from '../../UI-Lib/Message-Box/messageModal.service';
import { UserInstanceService } from '../../User/userInstance.service';
import { ContactsUpgradeService } from '../contacts.service.upgrade';

export interface ContactModalParams {
    contactId: number,
    entityInfo: any,
    roles: any,
    origin: ContactModalOrigin
}

@Component({
    selector: 'contact-modal',
    templateUrl: './contactModal.component.html',
    standalone: false
})
export class ContactModalComponent implements IWeissmanModalComponent<ContactModalParams, Weissman.Model.Contacts.Contact>, OnInit, OnDestroy {
    constructor(public readonly bsModalRef: BsModalRef,
                private readonly _userService: UserService,
                private readonly _contactsService: ContactsUpgradeService,
                private readonly _restrictService: RestrictService,
                private readonly _instanceRepository: InstanceRepository,
                private readonly _userInstanceService: UserInstanceService,
                private readonly _featureFlagsService: FeatureFlagsService,
                private readonly _roleService: ContactRoleService,
                private readonly _siteRepository: SiteRepository,
                private readonly _parcelRepository: ParcelRepository,
                private readonly _assessorCollectorRepository: AssessorCollectorRepository,
                private readonly _currentEntityService: CurrentEntityService,
                private readonly _router: UIRouter,
                private readonly _messageModalService: MessageModalService,
                private readonly _toastr: ToastrService,
                private readonly _fb: FormBuilder) {
    }

    params: ContactModalParams;
    result: Weissman.Model.Contacts.Contact;

    contactForm: FormGroup;
    editMode: boolean;
    tabEditMode: boolean;
    userSetupSelected: boolean;

    aceTabs: TabSetInput;

    contact: Weissman.Model.Contacts.Contact;
    contactRole;
    instanceId: number = null;
    prefixes: string[] = ['', 'Mr.', 'Ms.', 'Miss', 'Mrs.', 'Dr.'];
    loading: boolean;
    deleted: boolean;
    isCompanyEntity: boolean = true;
    hasEditPermission: boolean = true;
    canManageUsers: boolean;
    canEditUserInfo: boolean;
    origin: ContactModalOrigin;
    search = null;
    iamIsMigrated: boolean;
    roles = [];
    availableRoles = [];
    isSingleInstanceSelected: boolean;
    loggedInUserId: string;
    contactId: number;
    entityInfo: any;
    currentEntity: any;

    private _originalContact: Weissman.Model.Contacts.Contact;
    private _destroy$: Subject<void> = new Subject();

    getCommentsModalData = () => {
        return {
            entityTypeID: EntityTypeIds.CONTACT,
            entityID: this.contact.contactID,
            description: `Contact: ${this.contact.firstName} ${this.contact.lastName}`,
            canEdit: this.hasEditPermission
        };
    };

    async ngOnInit(): Promise<void> {
        this.roles = this.params.roles ? this.params.roles : [];
        this.origin = this.params.origin;
        this.contactId = this.params.contactId;
        this.entityInfo = this.params.entityInfo;

        this.loading = true;

        this.aceTabs = {
            activeTab: 'Roles',
            fillHeight: true
        };

        //TODO: get instance id from contact's company id
        //this.canManageUsers = restrictService.hasInstanceRight(InstanceRights.ManageUserSetup, instanceId);

        this.isSingleInstanceSelected = this._userService.isSingleInstanceSelected();

        this.loggedInUserId = this._userService.getUser().id;

        if (this.roles) {
            this.roles.sort((a, b) => {
                return a.roleDesc.localeCompare(b.roleDesc);
            });
        }

        try {
            // View/Edit existing Contact
            if (this.contactId) {
                await this._loadContact();
            }
            //Add new Contact
            else if (this.entityInfo) {
                if (this.origin !== ContactModalOrigin.NewContact) throw 'Adding new contact is only supported from NewContact button on Company panel for now';

                //check for Write entity permission
                const restrictionData = {
                    flag: Core.AccessRightsEnum.Write,
                    entityId: this.entityInfo.id
                };

                this.canEditUserInfo = true;

                this.hasEditPermission = await this._restrictService.hasPermission(restrictionData);

                //check instance right
                this.instanceId = await lastValueFrom(this._instanceRepository.getEntityInstanceId('company', this.entityInfo.id));
                this.canManageUsers = this._restrictService.hasInstanceRight(InstanceRights.MANAGEUSERSETUP, this.instanceId);
                await this._showNewContactForm();
            } else {
                // Should have either contactId or entityInfo.
                console.error('We don\'t have enough info here..., this.entityInfo is undefined or null');
            } // ToDo: Add a catch block.
        } finally {
            if (this.contact) {
                this.contactForm = this._fb.group({
                    prefix: [''],
                    firstName: [{
                        value: '',
                        disabled: !this.canEditUserInfo || this.iamIsMigrated
                    }, [Validators.required]],
                    lastName: [{ value: '', disabled: !this.canEditUserInfo || this.iamIsMigrated }, [Validators.required]],
                    title: [{ value: '', disabled: this.iamIsMigrated }],
                    address: this._fb.group({
                        address1: [''],
                        address2: [''],
                        city: [''],
                        stateID: [null],
                        zip: [''],
                        phone: [''],
                        ext: ['', [Validators.maxLength(10)]],
                        mobilePhone: [{ value: '', disabled: this.iamIsMigrated }],
                        fax: [''],
                        email: [
                            { value: '', disabled: !this.canEditUserInfo || this.iamIsMigrated },
                            (this.contact.userID ? [Validators.required, Validators.email] : [Validators.email])
                        ]
                    }),
                    active: [true],
                    companyID: [null],
                    contactRole: [this.contactRole]
                });

                this.contactForm.patchValue(this.contact);

                this.contactForm.valueChanges.pipe(takeUntil(this._destroy$))
                    .subscribe(() => {
                        const raw = this.contactForm.getRawValue();
                        this.contact.prefix = raw.prefix;
                        this.contact.firstName = raw.firstName;
                        this.contact.lastName = raw.lastName;
                        this.contact.title = raw.title;
                        this.contact.address = { ...this.contact.address, ...raw.address };
                        this.contact.active = raw.active;
                        this.contact.companyID = raw.companyID;
                    });
            }

            this.loading = false;
        }
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    async closeModal(): Promise<void> {
        if (this.editMode || this.tabEditMode) {
            try {
                await this._messageModalService.confirm('Editing is in progress. Are you sure you want to leave?');
            } catch (e) {
                return;
            }
        }

        this.bsModalRef.hide();
    }

    async saveContact(): Promise<void> {
        if (this.contact.prefix === '') {
            this.contact.prefix = null;
        }

        if (this.contact.contactID) {
            try {
                let updatedContact: Core.ContactLongDTO;
                if (this.origin === ContactModalOrigin.EntityContact) {
                    updatedContact = await this._contactsService.updateScoped(this.contact);
                } else {
                    updatedContact = await this._contactsService.update(this.contact);
                }

                this.result = this._copyDTOtoContact(updatedContact);
                await this._loadContact();
                this.editMode = false;
            } catch (e) {
                this._toastr.error(e.error.message);
                this.editMode = true;
            }

        } else {
            const roleToSave = {
                contactRoleID: this.contactForm.get('contactRole')?.value?.contactRoleID,
                entityType: this.entityInfo.type,
                entityID: Number(this.entityInfo.id)
            } as Weissman.Model.Contacts.ContactRoleAssociation;

            this.contact.companyID = Number(this.entityInfo.id);
            this.contact.contactRoleAssociations = [roleToSave];

            try {
                const updatedContact = await this._contactsService.create(this.contact);
                this.result =  updatedContact;
                this.contactId = updatedContact.contactID;
                this.origin = ContactModalOrigin.EntityContact;
                await this._loadContact();
                this.editMode = false;
            } catch (err) {
                if (err.status === 409) {
                    this._toastr.error('Error!', 'Duplicate Entry');
                } else {
                    this._toastr.error(err.error.message);
                }
                this.editMode = true;
            }
        }
    }

    async deleteContact(): Promise<void> {
        try {
            await this._messageModalService.confirm('Are you sure you want to delete this contact?');
        } catch (e) {
            return;
        }

        this.deleted = true;
        await this._contactsService.deleteContact(this.contact.contactID);
        this.bsModalRef.hide();
    }

    cancelSave(): void {
        if (this.origin === ContactModalOrigin.NewContact) {
            this.closeModal();
        } else {
            this.contact = structuredClone(this._originalContact);
            this.contactForm.patchValue(this._originalContact);
            this.editMode = false;
        }
    }

    async navigateEntity(role) {
        this.loading = true;

        try {
            const params = await this.getParams(role);
            this._router.stateService.transitionTo(role.entityType, params);
            this.bsModalRef.hide();
        } finally {
            this.loading = false;
        }

    }

    // TODO: Temporary fix until we overhaul URIs.
    async getParams(role) {
        const config = {
            // This is not a good place to cache, as entity associations can change. That said,
            // when I investigated this code, I couldn't find anywhere where we actually use
            // navigateEntity, which is the only place we call this function. So, probably the
            // best fix is to just remove these two functions. Alternatively, if I just missed
            // it and we really need it, there is now a route for things like /parcel/[parcelID]
            // without the site or company, so we should use that instead.
            //cache: true
        };
        switch (role.entityType) {
            case 'company':
                return {
                    companyId: role.entityID
                };
            case 'site':
                const site = await lastValueFrom(this._siteRepository.getSite(role.entityID, config));
                return {
                    companyId: site.companyID,
                    siteId: site.siteID
                };
            case 'parcel':
                const parcel = await lastValueFrom(this._parcelRepository.getContactParcel(role.entityID, config));
                return {
                    companyId: parcel.companyID,
                    siteId: parcel.siteID,
                    parcelId: parcel.parcelID
                };
            case 'assessor':
                const assessor = await lastValueFrom(this._assessorCollectorRepository.getAssessor(role.entityID));
                return {
                    stateId: assessor.stateID
                };
            case 'collector':
                const collector = await lastValueFrom(this._assessorCollectorRepository.getCollector(role.entityID));
                return {
                    stateId: collector.stateID
                };
        }
    }

    private async _loadContact(): Promise<void> {
        switch (this.origin) {
            case ContactModalOrigin.ViewProfile:
            case ContactModalOrigin.ContactsList:

                this.contact = await this._contactsService.getById(this.contactId);
                await this._processContact();
                //set callback used by signature panel/user picker for authorized users
                this.search = {
                    find: (filter) => {
                        return this._contactsService.search(this.instanceId, filter, 1, 100, true);
                    }
                };

                break;

            case ContactModalOrigin.EntityContact:
                this.contact = await this._contactsService.getByIdScoped(this.contactId);
                await this._processContact();

                //set callback used by signature panel/user picker for authorized users
                this.search = {
                    find: (filter) => {
                        return this._contactsService.searchForSignatureDelegates(this.contact.contactID, filter);
                    }
                };

                break;

            case ContactModalOrigin.NewContact: //just check someone has instance rights in relevant instance
                this.editMode = true;
                break;

            default:
                throw `Origin type ${origin} not handled`;
        }
    }

    private async _showNewContactForm(): Promise<void> {
        //New Contact
        const contact = {
            // TODO: change for other entities
            companyID: this.entityInfo.id,
            entityName: this.entityInfo.name,
            active: true,
            prefix: '',
            address: {}
        };

        if (this.roles && this.roles.length != 0) {
            this.availableRoles = this.roles;
            this.contactRole = this.availableRoles[0];
        } else {
            //TODO, put roleService.get into getAvailableRolesForContactEntityCombination. Roles aren't meant for the view in any of these cases
            const result = await this._roleService.get();
            this.availableRoles = this._contactsService.getAvailableRoles(result, contact, this.entityInfo);
            this.contactRole = this.availableRoles[0];
        }
        this.contact = this._assignCurrentEntityPropertiesToNewContact(contact);
        this.editMode = true;
    }

    private async _processContact(): Promise<void> {
        this.instanceId = this.contact.company.instanceID;
        this.hasEditPermission = this._restrictService.hasInstanceRight(InstanceRights.CONTACTEDIT, this.instanceId);
        this.canManageUsers = this._restrictService.hasInstanceRight(InstanceRights.MANAGEUSERSETUP, this.instanceId);
        this.contact.prefix = !this.contact.prefix || this.contact.prefix.trim() === '' ? '' : this.contact.prefix.trim();

        // if (!this.entityInfo) {
        // 	// this is existing contact, we don't really care about entity, but we have to show entity name
        // 	this.entityInfo = {
        // 		name: this.contact.entityName
        // 	}
        // }

        if (!this.contact.userID ||
            (this._restrictService.hasInstanceRight(InstanceRights.MANAGEUSERSETUP, this._userInstanceService.SystemInstanceId)
                && this._userInstanceService.isCurrentInstanceSystem())) {
            this.canEditUserInfo = true;
        }

        this.iamIsMigrated = this.contact.isMigrated && this._featureFlagsService.featureFlags.enableIAM;

        this._originalContact = structuredClone(this.contact);
    }

    private _assignCurrentEntityPropertiesToNewContact(contact) {
        contact.active = true;

        this.currentEntity = this._currentEntityService.get();
        if (!this.currentEntity) {
            return contact;
        }

        contact.entityName = this.currentEntity.name;
        contact.address = this.currentEntity.address;
        contact.companyID = this.entityInfo.id;

        contact.active = true;

        console.log('assignCurrentEntityPropertiesToNewContact - contact: ', contact);

        //TODO: Should be consistent ID vs Id
        if (contact.address) {
            contact.address.stateID = contact.address.stateId || 1;
            contact.address.addressID = 0;

            delete contact.address.stateId;
            delete contact.address.addressId;
            delete contact.address.state;
        }

        return contact;
    }

    private _copyDTOtoContact(dto: Core.ContactLongDTO): Weissman.Model.Contacts.Contact {
        // This is a mess, hopefully we can refactor these requests to return a Contact object instead of a DTO
        this.contact.active = dto.active;

        this.contact.address.abbr = dto.address.abbr;
        this.contact.address.address1 = dto.address.address1;
        this.contact.address.address2 = dto.address.address2;
        this.contact.address.city = dto.address.city;
        // this.contact.address.stateID = dto.address.stateID; This is not returned for some reason
        this.contact.address.zip = dto.address.zip;
        this.contact.address.email = dto.email;
        this.contact.address.ext = dto.ext;
        this.contact.address.phone = dto.phone;
        this.contact.address.mobilePhone = dto.mobilePhone;

        this.contact.contactID = dto.contactID;
        this.contact.entityName = dto.entityName;
        this.contact.firstName = dto.firstName;
        this.contact.lastName = dto.lastName;
        this.contact.title = dto.title;

        return this.contact;
    }
}
